// ── Community Spaces ──────────────────────────────────────────────
//
// Built on NIP-72 (kind 34550) for community definitions.
//
// Custom kinds for Community Spaces features:
//   34550  – Community definition (NIP-72, addressable/replaceable)
//   4550   – Moderator approval / post removal
//   1111   – Community post (NIP-22 comment)
//   30078  – Membership event (addressable, d-tag = "bchnostr/community-member/<a-tag>")
//
// Community Types:
//   "open"    – Free to join for any verified user
//   "premium" – Requires a BCH join fee (min $0.50)
//
// Revenue Model:
//   Creation fee ($3):  100% → BCH Nostr (platform)
//   Join fee (premium): 80% → Community Owner, 20% → BCH Nostr
//   Tips (in spaces):   80% → Community Owner, 20% → BCH Nostr

import { PLATFORM_FEE_ADDRESS } from './platform';

// ── Kinds ────────────────────────────────────────────────────────

export const COMMUNITY_DEFINITION_KIND = 34550;
export const COMMUNITY_APPROVAL_KIND = 4550;
export const COMMUNITY_POST_KIND = 1111;
export const MEMBERSHIP_KIND = 30078; // addressable event for membership

// ── Constants ────────────────────────────────────────────────────

/** One-time fee in USD to create a community */
export const COMMUNITY_CREATION_FEE_USD = 3;

/** Minimum trust score required to create a community */
export const COMMUNITY_CREATION_MIN_TRUST = 100;

/** Revenue split for join fees and tips (80% owner, 20% platform) */
export const OWNER_REVENUE_PERCENT = 0.80;
export const PLATFORM_REVENUE_PERCENT = 0.20;

/** Platform wallet for receiving platform's share */
export const PLATFORM_WALLET = PLATFORM_FEE_ADDRESS;

/** Minimum join fee in USD for premium communities */
export const MIN_JOIN_FEE_USD = 0.50;

// ── Types ────────────────────────────────────────────────────────

export type CommunityType = 'open' | 'premium';

export interface Community {
  /** The `a` tag coordinate: "34550:<pubkey>:<d-tag>" */
  id: string;
  /** d-tag identifier */
  dTag: string;
  /** Display name */
  name: string;
  /** Description */
  description: string;
  /** Rules */
  rules: string;
  /** Image/banner URL */
  image: string;
  /** Creator pubkey (= owner) */
  creator: string;
  /** Moderator pubkeys (includes creator) */
  moderators: string[];
  /** Community type */
  type: CommunityType;
  /** Join fee in USD (only for premium) */
  joinFeeUsd: number;
  /** Owner's BCH address for receiving revenue */
  ownerAddress: string;
  /** Created timestamp */
  createdAt: number;
  /** Archived flag */
  archived: boolean;
  /** Raw event for reference */
  raw?: any;
}

export interface MembershipData {
  /** The community a-tag this membership is for */
  communityId: string;
  /** Member pubkey */
  pubkey: string;
  /** Join timestamp */
  joinedAt: number;
  /** Payment tx ID (for premium communities) */
  txid?: string;
  /** Amount paid in sats (for premium communities) */
  paidSats?: number;
}

// ── Parsing ──────────────────────────────────────────────────────

/**
 * Parse a kind 34550 event into a Community object.
 */
export function parseCommunity(event: any): Community {
  const getTag = (name: string) => event.tags?.find((t: string[]) => t[0] === name)?.[1] || '';

  const dTag = getTag('d');
  const name = getTag('name') || dTag;
  const description = getTag('description') || '';
  const rules = getTag('rules') || '';
  const image = getTag('image') || '';
  const communityType = (getTag('community_type') || 'open') as CommunityType;
  const joinFeeStr = getTag('join_fee_usd');
  const joinFeeUsd = joinFeeStr ? parseFloat(joinFeeStr) : 0;
  const ownerAddress = getTag('owner_address') || '';
  const archived = getTag('status') === 'archived';

  const moderators = event.tags
    ?.filter((t: string[]) => t[0] === 'p' && t[3] === 'moderator')
    .map((t: string[]) => t[1]) || [];

  // Creator is always a moderator
  if (!moderators.includes(event.pubkey)) {
    moderators.unshift(event.pubkey);
  }

  return {
    id: `${COMMUNITY_DEFINITION_KIND}:${event.pubkey}:${dTag}`,
    dTag,
    name,
    description,
    rules,
    image,
    creator: event.pubkey,
    moderators,
    type: communityType,
    joinFeeUsd,
    ownerAddress,
    createdAt: event.created_at,
    archived,
    raw: event,
  };
}

/**
 * Parse a membership event (kind 30078) into MembershipData.
 *
 * Supports two formats:
 *   1. Self-published:  d = "bchnostr/community-member/<communityId>"
 *      → pubkey = event.pubkey (the member themselves)
 *   2. Admin-granted:   d = "bchnostr/community-member/<communityId>:grant:<targetPubkey>"
 *      → pubkey = targetPubkey (from the d-tag suffix / content.grantedFor)
 */
export function parseMembership(event: any): MembershipData | null {
  const dTag = event.tags?.find((t: string[]) => t[0] === 'd')?.[1] || '';
  if (!dTag.startsWith('bchnostr/community-member/')) return null;

  const remainder = dTag.replace('bchnostr/community-member/', '');

  // Check for admin-grant format: "<communityId>:grant:<targetPubkey>"
  const grantMatch = remainder.match(/^(.+):grant:([0-9a-f]{64})$/i);

  let communityId: string;
  let memberPubkey: string;

  if (grantMatch) {
    communityId = grantMatch[1];
    memberPubkey = grantMatch[2];
  } else {
    communityId = remainder;
    memberPubkey = event.pubkey;
  }

  let txid: string | undefined;
  let paidSats: number | undefined;
  try {
    const data = JSON.parse(event.content);
    txid = data.txid;
    paidSats = data.paidSats;
    // Fallback: use grantedFor from content if d-tag parsing didn't find it
    if (!grantMatch && data.grantedFor && /^[0-9a-f]{64}$/i.test(data.grantedFor)) {
      memberPubkey = data.grantedFor;
    }
  } catch {}

  return {
    communityId,
    pubkey: memberPubkey,
    joinedAt: event.created_at,
    txid,
    paidSats,
  };
}

// ── Tag Builders ─────────────────────────────────────────────────

/**
 * Build the `a` tag value for a community.
 */
export function communityATag(creatorPubkey: string, dTag: string): string {
  return `${COMMUNITY_DEFINITION_KIND}:${creatorPubkey}:${dTag}`;
}

/**
 * Build the d-tag for a membership event.
 */
export function membershipDTag(communityId: string): string {
  return `bchnostr/community-member/${communityId}`;
}

/**
 * Build tags for a community definition event.
 */
export function buildCommunityDefinitionTags(opts: {
  dTag: string;
  name: string;
  description: string;
  rules: string;
  image?: string;
  creatorPubkey: string;
  moderators?: string[];
  type: CommunityType;
  joinFeeUsd?: number;
  ownerAddress?: string;
  archived?: boolean;
}): string[][] {
  const tags: string[][] = [
    ['d', opts.dTag],
    ['name', opts.name],
    ['description', opts.description],
    ['rules', opts.rules],
    ['community_type', opts.type],
    ['p', opts.creatorPubkey, '', 'moderator'],
  ];

  if (opts.image) tags.push(['image', opts.image]);
  if (opts.type === 'premium' && opts.joinFeeUsd) {
    tags.push(['join_fee_usd', String(opts.joinFeeUsd)]);
  }
  if (opts.ownerAddress) tags.push(['owner_address', opts.ownerAddress]);
  if (opts.archived) tags.push(['status', 'archived']);

  // Add additional moderators (not the creator)
  if (opts.moderators) {
    for (const pk of opts.moderators) {
      if (pk !== opts.creatorPubkey) {
        tags.push(['p', pk, '', 'moderator']);
      }
    }
  }

  // Add topic tags for discoverability
  tags.push(['t', 'bch-community']);
  tags.push(['t', 'bitcoincash']);

  return tags;
}

/**
 * Build tags for a new top-level post in a community (kind 1111).
 */
export function buildCommunityPostTags(community: Community): string[][] {
  const aTag = communityATag(community.creator, community.dTag);
  return [
    ['A', aTag],
    ['a', aTag],
    ['P', community.creator],
    ['p', community.creator],
    ['K', String(COMMUNITY_DEFINITION_KIND)],
    ['k', String(COMMUNITY_DEFINITION_KIND)],
  ];
}

/**
 * Build tags for a reply to a post within a community (kind 1111).
 */
export function buildCommunityReplyTags(
  community: Community,
  parentEventId: string,
  parentAuthorPubkey: string,
  parentKind: number = COMMUNITY_POST_KIND,
): string[][] {
  const aTag = communityATag(community.creator, community.dTag);
  return [
    ['A', aTag],
    ['P', community.creator],
    ['K', String(COMMUNITY_DEFINITION_KIND)],
    ['e', parentEventId, '', 'reply'],
    ['p', parentAuthorPubkey],
    ['k', String(parentKind)],
  ];
}

/**
 * Build an approval event (kind 4550) for a moderator to approve a post.
 */
export function buildApprovalTags(
  community: Community,
  postEvent: any,
): string[][] {
  const aTag = communityATag(community.creator, community.dTag);
  return [
    ['a', aTag],
    ['e', postEvent.id],
    ['p', postEvent.pubkey],
    ['k', String(postEvent.kind)],
  ];
}

/**
 * Build a removal event (kind 4550 with removal marker) for moderation.
 */
export function buildRemovalTags(
  community: Community,
  postEvent: any,
  reason: string = '',
): string[][] {
  const aTag = communityATag(community.creator, community.dTag);
  return [
    ['a', aTag],
    ['e', postEvent.id],
    ['p', postEvent.pubkey],
    ['k', String(postEvent.kind)],
    ['action', 'remove'],
    ...(reason ? [['reason', reason]] : []),
  ];
}

/**
 * Build a member removal event (kind 4550 with member-remove action).
 */
export function buildMemberRemovalTags(
  community: Community,
  memberPubkey: string,
  reason: string = '',
): string[][] {
  const aTag = communityATag(community.creator, community.dTag);
  return [
    ['a', aTag],
    ['p', memberPubkey],
    ['action', 'remove-member'],
    ...(reason ? [['reason', reason]] : []),
  ];
}

/**
 * Build a member reinstatement event (kind 4550 with reinstate-member action).
 * This reverses a previous remove-member action.
 */
export function buildMemberReinstateTags(
  community: Community,
  memberPubkey: string,
  reason: string = '',
): string[][] {
  const aTag = communityATag(community.creator, community.dTag);
  return [
    ['a', aTag],
    ['p', memberPubkey],
    ['action', 'reinstate-member'],
    ...(reason ? [['reason', reason]] : []),
  ];
}

// ── Helpers ──────────────────────────────────────────────────────

/**
 * Check if a pubkey is the community owner.
 */
export function isOwner(community: Community, pubkey: string): boolean {
  return community.creator === pubkey;
}

/**
 * Check if a pubkey is a moderator (includes the owner).
 */
export function isModerator(community: Community, pubkey: string): boolean {
  return community.moderators.includes(pubkey) || community.creator === pubkey;
}

/**
 * Generate a URL-safe slug from a community name.
 */
export function communitySlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50);
}

/**
 * Calculate the 80/20 revenue split for join fees and tips.
 * 80% goes to the community owner, 20% to the platform.
 */
export function calculateTipRevenueSplit(totalSats: number): { ownerSats: number; platformSats: number } {
  const platformSats = Math.ceil(totalSats * PLATFORM_REVENUE_PERCENT);
  const ownerSats = totalSats - platformSats;
  return { ownerSats, platformSats };
}

/**
 * Convert USD to sats at current BCH price.
 */
export function usdToSats(usd: number, bchPriceUsd: number | null): number | null {
  if (!bchPriceUsd || bchPriceUsd <= 0) return null;
  return Math.ceil((usd / bchPriceUsd) * 1e8);
}

/**
 * Convert sats to USD display string.
 */
export function satsToUsdDisplay(sats: number, bchPriceUsd: number | null): string {
  if (!bchPriceUsd || bchPriceUsd <= 0) return `${sats.toLocaleString()} sats`;
  const usd = (sats / 1e8) * bchPriceUsd;
  return `$${usd.toFixed(2)}`;
}

// ── Eligibility Checks ──────────────────────────────────────────

export interface EligibilityResult {
  eligible: boolean;
  requirements: { met: boolean; label: string }[];
}

/**
 * Check if a user can create a community.
 * Requirements: verified account + $3 creation fee.
 * Trust score requirement removed — the $3 fee is sufficient spam prevention.
 */
export function canCreateCommunity(
  isVerified: boolean,
): EligibilityResult {
  const requirements = [
    { met: isVerified, label: 'Verified BCH Nostr account' },
    { met: true, label: `Pay $${COMMUNITY_CREATION_FEE_USD} BCH creation fee` }, // payment is checked at payment step
  ];

  return {
    eligible: requirements.every(r => r.met),
    requirements,
  };
}

/**
 * Check if a user can join a community.
 */
export function canJoinCommunity(isVerified: boolean): { eligible: boolean; reason?: string } {
  if (!isVerified) {
    return { eligible: false, reason: 'Verification is required to join and participate in Communities.' };
  }
  return { eligible: true };
}

